import { useState, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useExpenses } from '../contexts/ExpenseContext';
import { CATEGORIES, MONTHS, ExpenseCategory } from '../types';
import { 
  FileText, 
  Trash2, 
  Download,
  Search,
  Filter,
  Eye,
  X,
  Loader2,
  Share2,
  ZoomIn,
  ZoomOut,
  RotateCcw
} from 'lucide-react';

export default function Expenses() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { expenses, isLoading, deleteExpense } = useExpenses();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory | 'all'>(
    (searchParams.get('category') as ExpenseCategory) || 'all'
  );
  const [selectedMonth, setSelectedMonth] = useState<number | 'all'>('all');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [viewingExpense, setViewingExpense] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // Get years from 2020 to current year
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const allYears: number[] = [];
    for (let year = currentYear; year >= 2020; year--) {
      allYears.push(year);
    }
    return allYears;
  }, []);

  // Filter expenses
  const filteredExpenses = useMemo(() => {
    return expenses.filter(expense => {
      if (selectedCategory !== 'all' && expense.category !== selectedCategory) return false;
      if (selectedMonth !== 'all' && expense.month !== selectedMonth) return false;
      if (expense.year !== selectedYear) return false;
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          expense.filename.toLowerCase().includes(search) ||
          expense.description?.toLowerCase().includes(search) ||
          expense.freightBillNumber?.toLowerCase().includes(search)
        );
      }
      return true;
    });
  }, [expenses, selectedCategory, selectedMonth, selectedYear, searchTerm]);

  const handleCategoryChange = (cat: ExpenseCategory | 'all') => {
    setSelectedCategory(cat);
    if (cat === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', cat);
    }
    setSearchParams(searchParams);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      setDeletingId(id);
      try {
        await deleteExpense(id);
      } finally {
        setDeletingId(null);
      }
    }
  };

  // Convert base64 to blob
  const base64ToBlob = (base64: string): Blob => {
    const parts = base64.split(',');
    const mime = parts[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
    const bstr = atob(parts[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleDownload = async (expense: typeof expenses[0]) => {
    setIsDownloading(true);
    try {
      const blob = base64ToBlob(expense.fileData);
      
      // Try Web Share API first (works well on mobile)
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], expense.filename, { type: expense.fileType });
        const shareData = { files: [file] };
        
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          setIsDownloading(false);
          return;
        }
      }
      
      // Fallback: Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = expense.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Download failed:', err);
      // Last resort: open in new tab
      window.open(expense.fileData, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShare = async (expense: typeof expenses[0]) => {
    try {
      const blob = base64ToBlob(expense.fileData);
      const file = new File([blob], expense.filename, { type: expense.fileType });
      
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: expense.filename,
          text: expense.description || `${expense.category} expense`
        });
      } else {
        // Fallback to download
        await handleDownload(expense);
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.error('Share failed:', err);
      }
    }
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.5, 4));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.5, 0.5));
  const handleResetZoom = () => setZoom(1);

  const closeModal = () => {
    setViewingExpense(null);
    setZoom(1);
  };

  const viewingExpenseData = viewingExpense 
    ? expenses.find(e => e.id === viewingExpense) 
    : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Expenses</h1>
          <p className="text-white/60 mt-1">
            {filteredExpenses.length} document{filteredExpenses.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by filename, description..."
            className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
          />
        </div>

        {/* Filter Row */}
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="w-5 h-5 text-white/60" />
          
          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => handleCategoryChange(e.target.value as ExpenseCategory | 'all')}
            className="px-3 py-2 bg-shuttle-dark border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-shuttle-sand transition-all"
          >
            <option value="all">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>

          {/* Month Filter */}
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
            className="px-3 py-2 bg-shuttle-dark border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-shuttle-sand transition-all"
          >
            <option value="all">All Months</option>
            {MONTHS.map((month, i) => (
              <option key={i} value={i + 1}>{month}</option>
            ))}
          </select>

          {/* Year Filter */}
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-2 bg-shuttle-dark border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-shuttle-sand transition-all"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Expenses List */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
        {filteredExpenses.length > 0 ? (
          <div className="divide-y divide-white/10">
            {filteredExpenses.map((expense) => {
              const category = CATEGORIES.find(c => c.value === expense.category);
              return (
                <div
                  key={expense.id}
                  className="flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center space-x-4 min-w-0">
                    <span className="text-2xl flex-shrink-0">{category?.icon || '📄'}</span>
                    <div className="min-w-0">
                      <p className="font-medium text-white truncate">
                        {expense.filename}
                      </p>
                      <p className="text-sm text-white/60 truncate">
                        {expense.description || category?.label} • {new Date(expense.date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    {expense.amount && (
                      <span className="text-white font-medium hidden sm:block">
                        ${expense.amount.toFixed(2)}
                      </span>
                    )}
                    <span className={`category-badge category-${expense.category} hidden sm:inline-flex`}>
                      {category?.label}
                    </span>
                    <button
                      onClick={() => setViewingExpense(expense.id)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      title="View"
                    >
                      <Eye className="w-4 h-4 text-white/60" />
                    </button>
                    <button
                      onClick={() => handleShare(expense)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      title="Share/Download"
                    >
                      <Share2 className="w-4 h-4 text-white/60" />
                    </button>
                    <button
                      onClick={() => handleDelete(expense.id)}
                      disabled={deletingId === expense.id}
                      className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                      title="Delete"
                    >
                      {deletingId === expense.id ? (
                        <Loader2 className="w-4 h-4 text-red-400 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4 text-red-400" />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center text-white/60">
            <FileText className="w-12 h-12 mx-auto mb-4 text-white/30" />
            <p>No expenses found</p>
            <p className="text-sm text-white/40 mt-1">Try adjusting your filters</p>
          </div>
        )}
      </div>

      {/* View Modal with Zoom */}
      {viewingExpenseData && (
        <div 
          className="fixed inset-0 bg-black/90 flex flex-col z-50"
          onClick={closeModal}
        >
          {/* Header */}
          <div 
            className="flex items-center justify-between p-4 bg-shuttle-dark/90 backdrop-blur-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-white truncate flex-1 mr-4">
              {viewingExpenseData.filename}
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleShare(viewingExpenseData)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Share to Google Drive / Files"
              >
                <Share2 className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={() => handleDownload(viewingExpenseData)}
                disabled={isDownloading}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Download"
              >
                {isDownloading ? (
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                ) : (
                  <Download className="w-5 h-5 text-white" />
                )}
              </button>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Zoom Controls */}
          {viewingExpenseData.fileType.startsWith('image/') && (
            <div 
              className="flex items-center justify-center space-x-4 py-2 bg-black/50"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleZoomOut}
                disabled={zoom <= 0.5}
                className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                <ZoomOut className="w-5 h-5 text-white" />
              </button>
              <span className="text-white text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
              <button
                onClick={handleZoomIn}
                disabled={zoom >= 4}
                className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                <ZoomIn className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={handleResetZoom}
                className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                <RotateCcw className="w-4 h-4 text-white" />
              </button>
            </div>
          )}

          {/* Image/Content Area - Scrollable and Zoomable */}
          <div 
            ref={imageContainerRef}
            className="flex-1 overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="min-h-full flex items-center justify-center p-4">
              {viewingExpenseData.fileType.startsWith('image/') ? (
                <img
                  src={viewingExpenseData.fileData}
                  alt={viewingExpenseData.filename}
                  className="max-w-none transition-transform duration-200"
                  style={{ 
                    transform: `scale(${zoom})`,
                    transformOrigin: 'center center'
                  }}
                  draggable={false}
                />
              ) : viewingExpenseData.fileType === 'application/pdf' ? (
                <iframe
                  src={viewingExpenseData.fileData}
                  title={viewingExpenseData.filename}
                  className="w-full h-[80vh] rounded-lg bg-white"
                />
              ) : (
                <div className="text-center py-8 text-white/60">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-white/30" />
                  <p>Preview not available</p>
                  <button
                    onClick={() => handleDownload(viewingExpenseData)}
                    className="mt-4 px-4 py-2 bg-shuttle-sand text-shuttle-dark rounded-lg font-medium hover:bg-amber-400 transition-colors"
                  >
                    Download File
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer with details */}
          <div 
            className="p-4 bg-shuttle-dark/90 backdrop-blur-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="text-center">
                <p className="text-white/50">Category</p>
                <p className="text-white font-medium">
                  {CATEGORIES.find(c => c.value === viewingExpenseData.category)?.label}
                </p>
              </div>
              <div className="text-center">
                <p className="text-white/50">Date</p>
                <p className="text-white font-medium">
                  {new Date(viewingExpenseData.date).toLocaleDateString()}
                </p>
              </div>
              {viewingExpenseData.amount && (
                <div className="text-center">
                  <p className="text-white/50">Amount</p>
                  <p className="text-white font-medium">${viewingExpenseData.amount.toFixed(2)}</p>
                </div>
              )}
              <div className="text-center">
                <p className="text-white/50">Size</p>
                <p className="text-white font-medium">
                  {(viewingExpenseData.fileSize / 1024).toFixed(1)} KB
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
